// Shared Organization-Services portal styling — mirrors the Prism Admin Portal
// design language so CloudTrail, Security Hub and StackSets read as one console.
//
// The Admin portal hardcodes a fixed CloudKeeper palette, sets fontFamily inline
// (Bitter serif titles, Metropolis body), draws status as a pastel-triad chip
// (pastel bg / saturated fg / `${fg}20` border), wraps content in bordered
// `Paper elevation={0}` cards, and styles data tables with an `#F9FBFD` head +
// Metropolis-600 navy cells + a staggered row entrance. These helpers centralize
// exactly those conventions for the portal.

// --- CloudKeeper palette (design tokens) ---
export const CK = {
  navy: '#253E66', // primary text / titles / table headers
  body: '#666666', // secondary body text
  muted: '#999999', // subtitles, captions, empty text
  disabled: '#BDBDBD',
  border: '#E5E5E5', // universal border / divider
  pageBg: '#F9FBFD', // page background / table head / inset panels
  surface: '#FFFFFF',
  hover: '#F0F4F8', // icon-button hover surface
  blue: '#4698D3',
  blueDark: '#3580B8',
  green: '#27AE60',
  amber: '#F39C12',
  red: '#E74C3C',
  redDark: '#C0392B',
  purple: '#8E44AD',
};

export const FONT_TITLE = 'Bitter, Georgia, serif';
export const FONT_BODY = 'Metropolis, sans-serif';

// --- Status chips: pastel-triad (bg / fg / `${fg}20` border) ---
// Intents map onto the Admin portal's semantic triads.
const TRIAD = {
  success: { fg: '#27AE60', bg: '#F0F9F4' },
  warning: { fg: '#F39C12', bg: '#FEF9E7' },
  error: { fg: '#E74C3C', bg: '#FDF2F2' },
  info: { fg: '#4698D3', bg: '#EBF5FB' },
  purple: { fg: '#8E44AD', bg: '#F5EBF8' },
  neutral: { fg: '#999999', bg: '#F5F5F5' },
};

// statusChipSx returns the sx for a small pastel-triad status Chip.
// Usage: <Chip size="small" label={formatStatusLabel(x)} sx={statusChipSx('success')} />
export function statusChipSx(intent = 'neutral') {
  const t = TRIAD[intent] || TRIAD.neutral;
  return {
    height: 24,
    fontSize: '0.75rem',
    fontFamily: FONT_BODY,
    fontWeight: 600,
    backgroundColor: t.bg,
    color: t.fg,
    border: `1px solid ${t.fg}20`,
    '& .MuiChip-icon': { color: t.fg },
  };
}

// Map a MUI-style color name ('success'|'warning'|'error'|'info'|'default'|...)
// to a triad intent — lets existing `color={statusColor(x)}` maps be reused.
export const intentFromColor = (c) => ({
  success: 'success',
  warning: 'warning',
  error: 'error',
  info: 'info',
  primary: 'info',
  secondary: 'purple',
  default: 'neutral',
}[c] || 'neutral');

// --- Data table (Admin style) ---
export const tableHeadRowSx = { backgroundColor: CK.pageBg };

export const headCellSx = {
  fontFamily: FONT_BODY,
  fontWeight: 600,
  fontSize: '0.875rem',
  color: CK.navy,
  borderBottom: `1px solid ${CK.border}`,
  whiteSpace: 'nowrap',
};

// rowSx(index) → staggered fade-in entrance + hover + last-row border drop.
// The per-row delay is capped so large/paginated tables (e.g. Findings, which
// loads 50 rows at a time and can grow to hundreds) never leave late rows
// invisible for seconds or keep the compositor busy — the first ~12 rows stagger
// in, the rest share the same small delay.
export const rowSx = (i = 0) => ({
  animation: `portalFadeInRow 0.3s ease-out ${Math.min(i, 12) * 0.03}s both`,
  '@keyframes portalFadeInRow': {
    from: { opacity: 0, transform: 'translateY(8px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  '&:hover': { backgroundColor: CK.pageBg },
  '&:last-child td': { borderBottom: 'none' },
});

export const bodyCellSx = {
  fontFamily: FONT_BODY,
  fontSize: '0.875rem',
  color: CK.navy,
  borderBottom: `1px solid ${CK.border}`,
};

// --- Containers ---
// Bordered section card (padded).
export const sectionCardSx = {
  backgroundColor: CK.surface,
  border: `1px solid ${CK.border}`,
  borderRadius: '8px',
  p: 3,
};

// List card (unpadded; clips a table to the rounded corners).
export const listCardSx = {
  backgroundColor: CK.surface,
  border: `1px solid ${CK.border}`,
  borderRadius: '8px',
  overflow: 'hidden',
};

// Inset highlighted panel inside a card.
export const insetPanelSx = {
  backgroundColor: CK.pageBg,
  border: `1px solid ${CK.border}`,
  borderRadius: '8px',
  p: 2.5,
};

// Section heading (Bitter, navy). Spread onto a <Typography>.
export const sectionHeadingSx = {
  fontFamily: FONT_TITLE,
  fontWeight: 600,
  fontSize: '1.1rem',
  color: CK.navy,
};

// Muted empty-state text.
export const emptyTextSx = {
  fontFamily: FONT_BODY,
  color: CK.muted,
};

// --- Layout rhythm ---
// The Admin portal stacks section cards 24px apart under a header card that
// also sits 24px above the first section. Portal pages must use the same gap
// so the two consoles scroll at the same rhythm.
export const SECTION_GAP = 24;

// Spread onto the motion.div (or Box) wrapping a non-first section card.
export const sectionGapSx = { mt: 3 };

// --- Stat tiles ---
// Tiles in a Grid row must be equal height with their footer pinned to the
// bottom, otherwise a row of cards with differing body lengths reads as ragged.
// Usage: <Paper sx={statTileSx}> …body… <Box sx={statTileFooterSx}>…</Box></Paper>
export const statTileSx = {
  ...sectionCardSx,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
};

// Pins a tile's action/footer row to the bottom of an equal-height tile.
export const statTileFooterSx = { mt: 'auto', pt: 2 };

// --- Rows that must survive a narrow viewport ---
// A left/right split (title on the left, actions on the right) that wraps
// instead of crushing its children.
export const splitRowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  flexWrap: 'wrap',
};

// A group of buttons/filters that wraps rather than overflowing.
export const actionRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  flexWrap: 'wrap',
};

// --- Dialogs ---
// Every portal dialog is width-stable and rounded like the Admin console's.
// Usage: <Dialog open={x} onClose={y} {...dialogProps}>
export const dialogProps = {
  fullWidth: true,
  maxWidth: 'sm',
  PaperProps: {
    sx: {
      borderRadius: '12px',
      border: `1px solid ${CK.border}`,
      backgroundColor: CK.surface,
    },
  },
};

// Dialog title band — pastel fill + Bitter title, matching the Admin console.
export const dialogTitleSx = {
  backgroundColor: CK.pageBg,
  borderBottom: `1px solid ${CK.border}`,
  fontFamily: FONT_TITLE,
  fontWeight: 600,
  fontSize: '1.25rem',
  color: CK.navy,
  py: 2,
  px: 3,
};

// Entrance for a Grid tile, as a plain inline style on a plain <div>.
//
// Deliberately NOT a motion variant. Tiles sit inside MUI <Grid container>/
// <Grid>, and a variant label does not propagate through those to a nested
// motion.div — the tile then keeps the library's inline `opacity: 0` forever
// and renders correctly sized but completely invisible. Pure CSS keyframes
// (`portalTileIn`, defined globally in App.css) cannot fail that way.
//
// The cascade comes from the DURATION, not a delay, and the keyframes move
// ONLY the transform — opacity is never animated. A tile is therefore fully
// opaque at every instant, including frame zero, so no interrupted, delayed or
// never-started animation can leave it blank. That property matters more here
// than a fade: this content went missing in production twice.
export const tileEntranceStyle = (index = 0) => ({
  height: '100%',
  animation: `portalTileIn ${(0.3 + Math.min(index, 12) * 0.08).toFixed(2)}s ease-out both`,
});
